/**
 * Payment Profile: the restaurant's Aster Gateway account and card terminals,
 * as stored from the gateway (GET /api/merchant/payment-settings). "Sync" re-reads
 * them from Aster Gateway (POST /api/merchant/payment-settings/refresh).
 * Roles, stations and checkout rules are edited in Payment Settings.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { CreditCard, Wifi, WifiOff, RefreshCw, CheckCircle, Monitor } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

import { API } from '@/config/api';

const ROLE_LABELS = { pos_register: 'POS register', kiosk: 'Kiosk', online: 'Online orders' };

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-mono break-all">{value || '—'}</p>
    </div>
  );
}

export default function PaymentProfilePage() {
  const context = useOutletContext() || {};
  const token = context.token || localStorage.getItem('backoffice_token');
  const restaurantId = context.activeRestaurantId || context.restaurant?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  const headers = useMemo(() => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(restaurantId ? { 'X-Merchant-ID': String(restaurantId) } : {}),
  }), [token, restaurantId]);

  const request = useCallback(async (path = '', options = {}) => {
    const res = await fetch(`${API}/merchant/payment-settings${path}`, { headers, ...options });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Payment profile is unavailable right now');
    return body;
  }, [headers]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setData(await request()); setError(null); } catch (e) { setError(e.message); }
      setLoading(false);
    })();
  }, [request]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const body = await request('/refresh', { method: 'POST' });
      setData(body);
      if (body.connectivity_error) toast.warning(`Terminal status unavailable: ${body.connectivity_error}`);
      else toast.success('Synced from Aster Gateway');
    } catch (e) { toast.error(e.message); }
    setSyncing(false);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="p-6"><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></div>;
  }

  const merchant = data.merchant;
  const primary = merchant?.processors?.find(p => p.is_default) || merchant?.processors?.[0];
  const terminals = (data.terminals || []).filter(t => t.is_active);

  return (
    <div className="p-6 space-y-6" data-testid="payment-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payment Profile</h1>
          <p className="text-muted-foreground mt-1">Your card terminals and online payments through Aster Gateway</p>
        </div>
        {data.connected && (
          <Button onClick={handleSync} disabled={syncing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync from Aster Gateway'}
          </Button>
        )}
      </div>

      {!data.connected ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CreditCard className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold">Card payments are not set up</h3>
            <p className="text-sm text-muted-foreground mt-1">Your card terminals appear here once Aster Gateway is connected. Contact chain29 support to get set up.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <CardTitle>{merchant.merchant_name || 'Merchant account'}</CardTitle>
                <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Aster Gateway connected</Badge>
              </div>
              <CardDescription>Last synced {merchant.synced_at ? new Date(merchant.synced_at).toLocaleString() : 'never'}</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Gateway account ID" value={merchant.hub_mid} />
              <Field label="Merchant ID" value={primary?.processor_mid} />
              <Field label="Account status" value={merchant.status} />
            </CardContent>
          </Card>

          {terminals.length === 0 && (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No card terminals are listed on your account yet.</CardContent></Card>
          )}
          {terminals.map(t => {
            const isVirtual = t.terminal_type === 'virtual';
            const online = (t.connectivity_status || '').toLowerCase() === 'online';
            return (
              <Card key={t.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {isVirtual ? <Monitor className="h-5 w-5 text-purple-600" /> : <CreditCard className="h-5 w-5 text-teal-600" />}
                      <div>
                        <p className="font-semibold text-sm">{t.station || t.name || (isVirtual ? 'Online payments' : 'Card terminal')}</p>
                        <p className="text-xs text-muted-foreground">{isVirtual ? 'Virtual terminal' : 'Card terminal'} · {ROLE_LABELS[t.role] || 'Not assigned'}</p>
                      </div>
                    </div>
                    {!isVirtual && (
                      <span className={`flex items-center gap-1 text-xs ${online ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />} {t.connectivity_status || 'unknown'}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <Field label="Terminal ID" value={t.tpn || t.epi} />
                    <Field label="Merchant ID" value={t.mid} />
                    {!isVirtual && <Field label="Equipment" value={t.equipment} />}
                    {!isVirtual && <Field label="Serial No." value={t.serial} />}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}
